import React, { useContext, useMemo } from 'react';
import {
  forEach, get, isEmpty, isNull, filter, sumBy, includes, set, orderBy,
} from 'lodash';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import DailyMenuPage from '../components/DailyMenuPage';
import useQueryString from '../../hooks/useQueryString';
import menuContext from '../contexts/menu';
import { DATA_SOURCE, FILTER, PRODUCT_GROUP_PROPERTY } from '../constants';

export default ({
  data: menuData,
  onProductClick,
}) => {
  const {
    date: selectedDate,
    filter: selectedFilter = FILTER.PRE_ORDER,
    type: selectedType,
  } = useQueryString();
  const { fridgeId: selectedFridgeId } = useParams();
  const { menuCache } = useContext(menuContext);
  const { productProviderInfo, onlineShops, fridges } = menuCache || {};

  const showInventoryStatusSign = useMemo(() => {
    if (isEmpty(selectedFilter)) return false;
    if (selectedFilter === FILTER.PRE_ORDER) return false;
    if (selectedFilter === FILTER.FRESH) return false;

    return true;
  }, [selectedFilter]);

  const isOpenOnNewTab = useMemo(() => selectedFilter === FILTER.FRESH, [selectedFilter]);

  const data = useMemo(() => {
    const result = {};

    forEach(
      filter(
        menuData,
        (product) => {
          const {
            arrangedDateAvailableDict,
            dataSource,
            redeemDateArrangedDateDict,
            redeemDateLatestRegisterTimeDict,
            type,
          } = product;

          // 過濾商品類別
          if (selectedType && type !== selectedType) return false;
          // 過濾商店類型
          if (!get(product, ['filterDict', selectedFilter], false)) return false;
          // 過濾日期
          if (includes([DATA_SOURCE.DAILY_FRESH_PRE_ORDER, DATA_SOURCE.ONLINE_SHOP], dataSource)) {
            if (isEmpty(redeemDateLatestRegisterTimeDict)) return false;

            let arrangedDate = null;
            let redeemDate = null;
            let available = null;

            if (selectedDate) {
              redeemDate = selectedDate;

              arrangedDate = get(redeemDateArrangedDateDict, [selectedDate], null);
            } else {
              let latestRegisterTime = null;
              forEach(
                redeemDateLatestRegisterTimeDict,
                (registerTime, valueRedeemDate) => {
                  if (moment().isAfter(registerTime)) return;
                  if (
                    registerTime < latestRegisterTime
                    || isNull(latestRegisterTime)
                  ) {
                    latestRegisterTime = registerTime;
                    arrangedDate = get(redeemDateArrangedDateDict, [valueRedeemDate], null);
                    redeemDate = valueRedeemDate;
                  }
                }
              );
            }

            available = get(arrangedDateAvailableDict, [arrangedDate], null);
            const latestRegisterTime = get(
              redeemDateLatestRegisterTimeDict,
              [redeemDate],
              null
            );

            if (
              isNull(latestRegisterTime)
              || moment().isAfter(latestRegisterTime)
            ) return false;

            if (dataSource === DATA_SOURCE.DAILY_FRESH_PRE_ORDER) {
              if (!available) return false;

              product.arrangedTime = +moment(arrangedDate).utcOffset(8);
            }

            product.redeemStartTime = +moment(redeemDate).utcOffset(8).startOf('day').set('hour', 12);
            product.redeemEndTime = +moment(redeemDate)
              .utcOffset(8)
              .startOf('day')
              .add(1, 'day')
              .set('hour', 10);
            product.fridgeId = selectedFridgeId;
            product.fridgeName = get(fridges, [selectedFridgeId, 'name']);
            product.locationPrompt = get(fridges, [selectedFridgeId, 'locationPrompt']);
            product.latestRegisterTime = latestRegisterTime;
          }

          return true;
        }
      ),
      (product) => {
        const { dataSource } = product;
        const groupProperty = PRODUCT_GROUP_PROPERTY[dataSource];

        if (isEmpty(groupProperty)) return;

        const groupValue = get(product, [groupProperty], null);

        if (isNull(groupValue)) return;

        const GROUP_INFO_MAP = {
          [DATA_SOURCE.MENU]: productProviderInfo,
          [DATA_SOURCE.DAILY_FRESH_PRE_ORDER]: productProviderInfo,
          [DATA_SOURCE.ONLINE_SHOP]: onlineShops,
        };

        const group = get(GROUP_INFO_MAP, [dataSource, groupValue], null);
        const { displayName, provider } = group;
        const { provider: productProvider } = product;

        // 如果有 displayName 就用 displayName，沒有就用 provider
        set(group, 'provider', displayName || provider);
        set(product, 'provider', displayName || productProvider);

        if (isNull(group)) return;

        if (isEmpty(result[groupValue])) {
          result[groupValue] = {
            ...group,
            id: groupValue,
            products: [],
          };
        }

        result[groupValue].products.push(product);
      }
    );

    return orderBy(result,
      (item) => [
        get(item, ['weight'], 0),
        sumBy(get(item, ['products'], null), 'inventory'),
      ],
      ['desc', 'asc']);
  }, [
    fridges,
    menuData,
    onlineShops,
    productProviderInfo,
    selectedDate,
    selectedFilter,
    selectedFridgeId,
    selectedType,
  ]);

  return (
    <DailyMenuPage
      isOpenOnNewTab={isOpenOnNewTab}
      data={data}
      onProductClick={onProductClick}
      showInventoryStatusSign={showInventoryStatusSign}
    />
  );
};
